// Post-refresh lifecycle for agent-owned PresLink tool calls.
//
// The main agent owns semantic intent and scope through apply_preslink tool
// arguments. This module owns only deterministic runtime mechanics after the
// destructive operation has replaced slides:
//   1. re-extract the chat window and the exact refreshed slide ids;
//   2. host-probe fresh shapes to re-identify PresLink-managed objects;
//   3. publish fresh ids and the managed-shape exclusion map to later tool hops.

import { CHAT_CONTEXT_WINDOW_SIZE } from "../../../shared/config/constants";
import type { PresentationContent, SlideContent } from "../../types";
import {
  extractPresentationContent,
  extractWindowedPresentationContent,
  type ExtractWindowedPresentationContentOptions,
} from "../slideService";
import type { AgentToolContext } from "./types";

type ShapeRef = { slideId: string; id: string };
type ExtractionFilters = ExtractWindowedPresentationContentOptions["filters"];

export interface PreslinkRefreshLifecycleParams {
  /** Presentation snapshot sent to the seed agent turn. */
  presentationContent?: PresentationContent;
  /** New host slide ids returned by the completed replacement operation. */
  refreshedSlideIds: string[];
  /** Shared turn state updated for subsequent tool hops. */
  agentToolContext: AgentToolContext;
  /** Extraction filters matching the seed chat context. */
  extractionFilters?: ExtractionFilters;
}

export interface PreslinkRefreshLifecycleDependencies {
  collectPotentialPreslinkShapeRefs: (content: PresentationContent) => ShapeRef[];
  collectLinkedPreslinkRefsFromPathsProbe: (options: {
    shapeRefs: ShapeRef[];
    ctx?: AgentToolContext;
  }) => Promise<ShapeRef[]>;
  applyPreslinkLinkedMarkers: (
    content: PresentationContent,
    linkedRefs: ShapeRef[]
  ) => PresentationContent;
  extractPresentationContent: typeof extractPresentationContent;
  extractWindowedPresentationContent: typeof extractWindowedPresentationContent;
}

export interface PreslinkRefreshLifecycleResult {
  freshPresentationContent: PresentationContent;
  freshScopeContent: PresentationContent;
  excludedShapeIdsBySlideId: Map<string, Set<string>>;
}

function buildScopedFilters(
  filters: ExtractionFilters,
  slideIds: string[]
): ExtractionFilters {
  const callerSlides =
    filters?.slides && typeof filters.slides === "object"
      ? ({ ...filters.slides } as Record<string, unknown>)
      : {};
  delete callerSlides.ranges;
  delete callerSlides.ids;
  return {
    ...(filters ?? {}),
    slides: {
      ...callerSlides,
      ids: slideIds,
    },
  } as ExtractionFilters;
}

function mergeFreshScopeIntoWindow(
  freshWindow: PresentationContent,
  freshScope: PresentationContent
): PresentationContent {
  const slidesByIndex = new Map<number, SlideContent>();
  for (const slide of freshWindow.slides) slidesByIndex.set(slide.index, slide);
  for (const slide of freshScope.slides) slidesByIndex.set(slide.index, slide);
  const totalDeckSlides = freshWindow.totalDeckSlides ?? freshScope.totalDeckSlides;
  return {
    ...freshWindow,
    slides: Array.from(slidesByIndex.values()).sort((left, right) => left.index - right.index),
    ...(totalDeckSlides !== undefined ? { totalDeckSlides } : {}),
  };
}

function buildExclusionMap(linkedRefs: ShapeRef[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();
  for (const ref of linkedRefs) {
    if (!ref.slideId || !ref.id) continue;
    const shapeIds = map.get(ref.slideId) ?? new Set<string>();
    shapeIds.add(ref.id);
    map.set(ref.slideId, shapeIds);
  }
  return map;
}

/**
 * Rebuild fresh state after a completed PresLink replacement. Any failure is
 * fatal for later native edits because the model's previous slide/shape ids are
 * stale and managed objects can no longer be protected safely.
 */
export async function finalizePreslinkRefreshLifecycle(
  params: PreslinkRefreshLifecycleParams,
  deps: PreslinkRefreshLifecycleDependencies
): Promise<PreslinkRefreshLifecycleResult> {
  const { agentToolContext: ctx } = params;
  if (ctx.signal?.aborted) {
    throw new Error("PresLink refresh was cancelled.");
  }

  const refreshedSlideIds = Array.from(
    new Set(
      params.refreshedSlideIds
        .map((slideId) => String(slideId || "").trim())
        .filter((slideId) => slideId.length > 0)
    )
  );
  if (refreshedSlideIds.length === 0) {
    throw new Error("PresLink replacement returned no refreshed slide ids.");
  }

  const windowAnchor = params.presentationContent
    ? {
        selectedSlideIndex: params.presentationContent.selectedSlideIndex,
        totalSlideCount:
          params.presentationContent.totalDeckSlides ??
          Math.max(1, params.presentationContent.slides.length),
      }
    : undefined;

  const freshWindow = await deps.extractWindowedPresentationContent(ctx.log, {
    forceRefresh: true,
    windowSize: CHAT_CONTEXT_WINDOW_SIZE,
    presentationRef: ctx.presentationRef ?? undefined,
    filters: params.extractionFilters,
    ...(windowAnchor ? { windowAnchor } : {}),
  });
  const freshScope = await deps.extractPresentationContent(ctx.log, {
    forceRefresh: true,
    presentationRef: ctx.presentationRef ?? undefined,
    filters: buildScopedFilters(params.extractionFilters, refreshedSlideIds),
  });

  const returnedIds = new Set(freshScope.slides.map((slide) => slide.id));
  const missingIds = refreshedSlideIds.filter((slideId) => !returnedIds.has(slideId));
  if (missingIds.length > 0) {
    throw new Error(
      `Fresh extraction omitted ${missingIds.length} refreshed PresLink slide(s).`
    );
  }

  const candidateRefs = deps.collectPotentialPreslinkShapeRefs(freshScope);
  if (candidateRefs.length === 0) {
    throw new Error("The refreshed PresLink scope contained no shapes to verify.");
  }
  const freshLinkedRefs = await deps.collectLinkedPreslinkRefsFromPathsProbe({
    shapeRefs: candidateRefs,
    ctx,
  });
  if (freshLinkedRefs.length === 0) {
    throw new Error("The refreshed PresLink-managed objects could not be re-identified.");
  }
  if (ctx.signal?.aborted) {
    throw new Error("PresLink refresh was cancelled.");
  }

  // Re-stamp the linkage marker onto the fresh ids. Both the follow-up envelope
  // and the tool result's `slidesInfo` are rebuilt from these extracts, so
  // without this the model loses the linkage signal right after a refresh.
  const freshScopeContent = deps.applyPreslinkLinkedMarkers(freshScope, freshLinkedRefs);
  const freshPresentationContent = mergeFreshScopeIntoWindow(freshWindow, freshScopeContent);
  const excludedShapeIdsBySlideId = buildExclusionMap(freshLinkedRefs);
  ctx.latestPresentationContent = freshPresentationContent;
  ctx.preslinkExcludedShapeIdsBySlideId = excludedShapeIdsBySlideId;
  ctx.preslinkRefreshFailed = false;
  ctx.reportPreslinkRefresh?.({
    presentationContent: freshPresentationContent,
    refreshedSlideIds,
    excludedShapeIdsBySlideId,
  });

  return {
    freshPresentationContent,
    freshScopeContent,
    excludedShapeIdsBySlideId,
  };
}
